import React from 'react';

/**
 * LockedIn sport emblems — neon-ring badge style (canonical concept).
 * Monochrome artwork inside a glowing sport-color ring with electric sparks.
 * Custom original art only: no team, league, or school marks
 * (College replaces any NCAA-style badge).
 */

const INK = '#07090d';
const PAPER = '#f7f9fc';
const DISC = '#0d1118';

function Emblem({ children, ...props }) {
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...props}
    >
      {children}
    </svg>
  );
}

/** Neon ring + inner disc shared by every badge. */
function NeonRing({ color }) {
  return (
    <>
      <circle cx="32" cy="32" r="24" stroke={color} strokeWidth="6" opacity="0.22" />
      <circle cx="32" cy="32" r="24" fill={DISC} stroke={color} strokeWidth="2.5" />
    </>
  );
}

/** Electric crack sparks around the ring. */
function RingSparks({ color, accent = PAPER }) {
  return (
    <>
      <g stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 15l4.5 3-2 2.5 5 3" />
        <path d="M55 13l-4.5 3.5 2.5 2-3.5 3" />
        <path d="M3.5 39l5-1-1 3.5 5.5-1.5" />
        <path d="M60.5 37l-5 .5 1.5 3.5-5.5-.5" />
        <path d="M16 57l3-4.5 3 2 2-4" />
        <path d="M48 56.5l-2.5-4.5-3 2.5-1.5-4.5" />
      </g>
      <g stroke={accent} strokeWidth="1.25" strokeLinecap="round" opacity="0.75">
        <path d="M22 6.5l1.5 3M41 5.5l-1 3.5M6 26l3 1.5M58 25l-3 1.5M31 60.5v-3" />
      </g>
    </>
  );
}

/** Basketball — orange ball, black seams, orange ring. */
export function BasketballEmblem(props) {
  const RING = '#f97316';
  return (
    <Emblem {...props}>
      <RingSparks color={RING} />
      <NeonRing color={RING} />
      <circle cx="32" cy="32" r="15.5" fill="#f97316" />
      <path d="M32 16.5v31M16.5 32h31" stroke={INK} strokeWidth="2" />
      <path
        d="M21.5 21.5c5.5 5 5.5 16 0 21M42.5 21.5c-5.5 5-5.5 16 0 21"
        stroke={INK}
        strokeWidth="1.75"
      />
      <path
        d="M23.5 21.5a14.5 14.5 0 0 1 6.5-4"
        stroke="#ffedd5"
        strokeWidth="2.25"
        strokeLinecap="round"
        opacity="0.8"
      />
    </Emblem>
  );
}

/** Football — crimson crest ball with spike burst, crimson ring. */
export function FootballEmblem(props) {
  const RING = '#dc2626';
  return (
    <Emblem {...props}>
      <RingSparks color={RING} />
      <NeonRing color={RING} />
      <polygon
        points="32,11 35,20 42,15 40,23 50,24 42,29 49,36 40,35 43,46 35,41 32,52 29,41 21,46 24,35 15,36 22,29 14,24 24,23 22,15 29,20"
        fill="#7f1d1d"
        opacity="0.8"
      />
      <ellipse cx="32" cy="32" rx="9.5" ry="14" fill="#b91c1c" stroke="#450a0a" strokeWidth="1.75" />
      <path d="M32 21v-2.5M32 45.5V43" stroke="#450a0a" strokeWidth="2.25" strokeLinecap="round" />
      <path
        d="M32 25.5v13M29 28.5h6M29 31.5h6M29 34.5h6M29 37.5h6"
        stroke={PAPER}
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </Emblem>
  );
}

/** Baseball — white ball, black stitches and splatter, royal-blue ring. */
export function BaseballEmblem(props) {
  const RING = '#3b82f6';
  return (
    <Emblem {...props}>
      <RingSparks color={RING} />
      <NeonRing color={RING} />
      <path
        d="M20 18c-3 4-5 9-4 14M46 47c3-4 4-9 3.5-13"
        stroke={INK}
        strokeWidth="4"
        strokeLinecap="round"
        opacity="0.85"
      />
      <circle cx="15.5" cy="40" r="1.6" fill={INK} />
      <circle cx="47" cy="20" r="1.8" fill={INK} />
      <circle cx="44" cy="51" r="1.3" fill={INK} />
      <circle cx="32" cy="32" r="14.5" fill={PAPER} />
      <path
        d="M21.5 22c4.8 4.4 4.8 15.6 0 20M42.5 22c-4.8 4.4-4.8 15.6 0 20"
        stroke={INK}
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M22 25l3 .9M20.6 28.8l3.2.5M20.2 32.5l3.3-.1M20.8 36.3l3.2-.6M22.2 39.8l2.9-1.2M42 25l-3 .9M43.4 28.8l-3.2.5M43.8 32.5l-3.3-.1M43.2 36.3l-3.2-.6M41.8 39.8l-2.9-1.2"
        stroke={INK}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path d="M27 24.5l7 11M30 23.5l4.5 7" stroke={INK} strokeWidth="1" strokeLinecap="round" opacity="0.5" />
    </Emblem>
  );
}

/** Soccer — white ball with black pentagons, emerald ring. */
export function SoccerEmblem(props) {
  const RING = '#10b981';
  return (
    <Emblem {...props}>
      <RingSparks color={RING} />
      <NeonRing color={RING} />
      <circle cx="32" cy="32" r="14.5" fill={PAPER} />
      <path d="M32 23l5.5 4-2.1 6.4h-6.8L26.5 27l5.5-4z" fill={INK} />
      <path
        d="M32 17.5V23M32 23l-9.2-2.8M26.6 33.4l-7 3.5M37.4 33.4l6.8 3.7M39.6 27l8.6-3.1M23 20.2l-4.6-1.6M43.6 44.2l-3.1-4.6M21.4 44.6l3.7-5.5"
        stroke={INK}
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M20.9 21.9l2.5 4.1M46.4 31a14 14 0 0 0-2-5.7"
        stroke={INK}
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.6"
      />
    </Emblem>
  );
}

/** Boxing — white padded glove raised, crimson ring. */
export function BoxingEmblem(props) {
  const RING = '#ef4444';
  return (
    <Emblem {...props}>
      <RingSparks color={RING} />
      <NeonRing color={RING} />
      {/* Cuff */}
      <path d="M26 42h12v6a2 2 0 0 1-2 2H28a2 2 0 0 1-2-2v-6z" fill={PAPER} />
      <path d="M26 44.5h12" stroke={INK} strokeWidth="1.5" />
      {/* Fist */}
      <path
        d="M23.5 27c0-6 4-10.5 9.5-10.5S42.5 21 42.5 27v9c0 3.5-2.5 6-6 6h-7c-3.5 0-6-2.5-6-6v-9z"
        fill={PAPER}
      />
      {/* Finger pads */}
      <path
        d="M27 17.5V26M32 16v10M37 17.5V26"
        stroke={INK}
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.8"
      />
      {/* Thumb */}
      <path
        d="M23.5 31c-2.8 0-4.5 2-4.5 4.3 0 2.5 2 4.2 4.5 4.2"
        stroke={PAPER}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path d="M24 30.5h18.5" stroke={INK} strokeWidth="1.5" opacity="0.8" />
    </Emblem>
  );
}

/** MMA — two fists clashing with impact burst, purple ring. */
export function MmaEmblem(props) {
  const RING = '#a855f7';
  return (
    <Emblem {...props}>
      <RingSparks color={RING} />
      <NeonRing color={RING} />
      {/* Impact burst */}
      <polygon
        points="32,15 34.5,23 41,18 38.5,26 47,25 40,30.5 48,34 39.5,34.5 43,43 35.5,38 34,47 30,38.5 24,45 26,36 17,37 24.5,31 16,27 25,26.5 21,18 28.5,23"
        stroke={PAPER}
        strokeWidth="1.5"
        strokeLinejoin="round"
        fill="none"
        opacity="0.9"
      />
      {/* Left fist */}
      <path
        d="M18 29.5c0-3.4 2.6-5.5 6-5.5 3.8 0 6.5 2.3 6.5 5.7 0 1.6-.6 3-1.7 4l-4.3 3c-1.4.8-3.2.6-4.5-.5-1.3-1.2-2-2.9-2-4.7v-2z"
        fill={PAPER}
      />
      <path d="M20.5 26.8c1-1.2 2.6-1.9 4.3-1.8" stroke={INK} strokeWidth="1.25" strokeLinecap="round" opacity="0.7" />
      {/* Right fist */}
      <path
        d="M46 29.5c0-3.4-2.6-5.5-6-5.5-3.8 0-6.5 2.3-6.5 5.7 0 1.6.6 3 1.7 4l4.3 3c1.4.8 3.2.6 4.5-.5 1.3-1.2 2-2.9 2-4.7v-2z"
        fill={PAPER}
      />
      <path d="M43.5 26.8c-1-1.2-2.6-1.9-4.3-1.8" stroke={INK} strokeWidth="1.25" strokeLinecap="round" opacity="0.7" />
    </Emblem>
  );
}

/** College — grad-cap badge (custom, no league marks), indigo ring. */
export function CollegeEmblem(props) {
  const RING = '#6366f1';
  return (
    <Emblem {...props}>
      <RingSparks color={RING} />
      <NeonRing color={RING} />
      <circle cx="32" cy="32" r="16" fill="#1e1b4b" />
      <path d="M32 23.5l12 5.2-12 5.2-12-5.2 12-5.2z" fill={PAPER} />
      <path
        d="M25.5 32v4.6c0 2 3 3.6 6.5 3.6s6.5-1.6 6.5-3.6V32"
        stroke={PAPER}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path d="M44 29v6.5" stroke={PAPER} strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="44" cy="37.5" r="1.5" fill="#fbbf24" />
    </Emblem>
  );
}

/** All sports — lightning bolt badge, emerald ring. */
export function AllSportsEmblem(props) {
  const RING = '#10b981';
  return (
    <Emblem {...props}>
      <RingSparks color={RING} accent="#34d399" />
      <NeonRing color={RING} />
      <path
        d="M36 17.5L23.5 33h7.5L26.5 46.5 40 30.5h-7.5L36 17.5z"
        fill="#34d399"
      />
    </Emblem>
  );
}

export const SPORT_EMBLEMS = Object.freeze({
  basketball: BasketballEmblem,
  football: FootballEmblem,
  baseball: BaseballEmblem,
  soccer: SoccerEmblem,
  boxing: BoxingEmblem,
  mma: MmaEmblem,
  college: CollegeEmblem,
  generic: AllSportsEmblem,
});
